/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { healthApi } from '../../services/api';
import type {
  WeightEntry,
  AddWeightEntryRequest,
  WaterIntake,
  AddWaterIntakeRequest,
  BodyMeasurement,
  AddBodyMeasurementRequest,
  HealthMetrics,
} from '../../types';

interface HealthState {
  weightEntries: WeightEntry[];
  waterIntake: WaterIntake[];
  bodyMeasurements: BodyMeasurement[];
  healthMetrics: HealthMetrics | null;
  dashboard: any;
  isLoading: boolean;
  error: string | null;
}

const initialState: HealthState = {
  weightEntries: [],
  waterIntake: [],
  bodyMeasurements: [],
  healthMetrics: null,
  dashboard: null,
  isLoading: false,
  error: null,
};

// Async thunks
export const getWeightEntries = createAsyncThunk(
  'health/getWeightEntries',
  async ({ startDate, endDate }: { startDate?: string; endDate?: string }, { rejectWithValue }) => {
    try {
      const entries = await healthApi.getWeightEntries(startDate, endDate);
      return entries;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to get weight entries');
    }
  }
);

export const addWeightEntry = createAsyncThunk(
  'health/addWeightEntry',
  async (data: AddWeightEntryRequest, { rejectWithValue }) => {
    try {
      const entry = await healthApi.addWeightEntry(data);
      return entry;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to add weight entry');
    }
  }
);

export const deleteWeightEntry = createAsyncThunk(
  'health/deleteWeightEntry',
  async (id: string, { rejectWithValue }) => {
    try {
      await healthApi.deleteWeightEntry(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to delete weight entry');
    }
  }
);

export const getWaterIntake = createAsyncThunk(
  'health/getWaterIntake',
  async (date: string, { rejectWithValue }) => {
    try {
      const intake = await healthApi.getWaterIntake(date);
      return intake;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to get water intake');
    }
  }
);

export const addWaterIntake = createAsyncThunk(
  'health/addWaterIntake',
  async (data: AddWaterIntakeRequest, { rejectWithValue }) => {
    try {
      const intake = await healthApi.addWaterIntake(data);
      return intake;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to add water intake');
    }
  }
);

export const getBodyMeasurements = createAsyncThunk(
  'health/getBodyMeasurements',
  async (_, { rejectWithValue }) => {
    try {
      const measurements = await healthApi.getBodyMeasurements();
      return measurements;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to get body measurements');
    }
  }
);

export const addBodyMeasurement = createAsyncThunk(
  'health/addBodyMeasurement',
  async (data: AddBodyMeasurementRequest, { rejectWithValue }) => {
    try {
      const measurement = await healthApi.addBodyMeasurement(data);
      return measurement;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to add body measurement');
    }
  }
);

export const getHealthMetrics = createAsyncThunk(
  'health/getHealthMetrics',
  async (_, { rejectWithValue }) => {
    try {
      const metrics = await healthApi.getHealthMetrics();
      return metrics;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to get health metrics');
    }
  }
);

export const getDashboard = createAsyncThunk(
  'health/getDashboard',
  async (date: string, { rejectWithValue }) => {
    try {
      const dashboard = await healthApi.getDashboard(date);
      return dashboard;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to get dashboard');
    }
  }
);

// Slice
const healthSlice = createSlice({
  name: 'health',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Get weight entries
    builder.addCase(getWeightEntries.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(getWeightEntries.fulfilled, (state, action) => {
      state.isLoading = false;
      state.weightEntries = action.payload;
      state.error = null;
    });
    builder.addCase(getWeightEntries.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Add weight entry
    builder.addCase(addWeightEntry.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(addWeightEntry.fulfilled, (state, action) => {
      state.isLoading = false;
      state.weightEntries.unshift(action.payload);
      state.error = null;
    });
    builder.addCase(addWeightEntry.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Delete weight entry
    builder.addCase(deleteWeightEntry.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(deleteWeightEntry.fulfilled, (state, action) => {
      state.isLoading = false;
      state.weightEntries = state.weightEntries.filter((w) => w.id !== action.payload);
      state.error = null;
    });
    builder.addCase(deleteWeightEntry.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Get water intake
    builder.addCase(getWaterIntake.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(getWaterIntake.fulfilled, (state, action) => {
      state.isLoading = false;
      state.waterIntake = action.payload;
      state.error = null;
    });
    builder.addCase(getWaterIntake.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Add water intake
    builder.addCase(addWaterIntake.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(addWaterIntake.fulfilled, (state, action) => {
      state.isLoading = false;
      state.waterIntake.push(action.payload);
      state.error = null;
    });
    builder.addCase(addWaterIntake.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Get body measurements
    builder.addCase(getBodyMeasurements.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(getBodyMeasurements.fulfilled, (state, action) => {
      state.isLoading = false;
      state.bodyMeasurements = action.payload;
      state.error = null;
    });
    builder.addCase(getBodyMeasurements.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Add body measurement
    builder.addCase(addBodyMeasurement.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(addBodyMeasurement.fulfilled, (state, action) => {
      state.isLoading = false;
      state.bodyMeasurements.unshift(action.payload);
      state.error = null;
    });
    builder.addCase(addBodyMeasurement.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Get health metrics
    builder.addCase(getHealthMetrics.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(getHealthMetrics.fulfilled, (state, action) => {
      state.isLoading = false;
      state.healthMetrics = action.payload;
      state.error = null;
    });
    builder.addCase(getHealthMetrics.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Get dashboard
    builder.addCase(getDashboard.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(getDashboard.fulfilled, (state, action) => {
      state.isLoading = false;
      state.dashboard = action.payload;
      state.error = null;
    });
    builder.addCase(getDashboard.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
  },
});

export const { clearError } = healthSlice.actions;
export default healthSlice.reducer;
